import {
  getArenaReservableSeatGroups,
  getArenaSendMethodSourceCode,
} from './arenaSeatSelectionUtils'
import type {
  ArenaSelectedSeat,
  ArenaSendMethod,
} from './arenaSeatSelectionUtils'

interface CreateArenaTrolleyTokenParams {
  apiAuthToken: string
  apiBase: string
  perfId: string
  selectedSeats: Array<ArenaSelectedSeat>
  sendMethod: ArenaSendMethod
}

type ArenaTrolleyResponse = {
  results?: {
    trolley_token?: string
  }
  trolley_token?: string
}

function getArenaTrolleyResponseToken(response: ArenaTrolleyResponse) {
  return response.trolley_token ?? response.results?.trolley_token
}

function buildTrolleyUrl(apiBase: string, params: URLSearchParams) {
  return `${apiBase.replace(/\/$/, '')}/trolley.v1?${params.toString()}`
}

function getArenaTrolleyLogContext(group: ReturnType<typeof getArenaReservableSeatGroups>[number], perfId: string) {
  return {
    noOfSeats: group.seats.length,
    perfId,
    priceBandCode: group.priceBandCode,
    seatIds: group.seats.map(seat => seat.seatId),
    ticketTypeCode: group.ticketTypeCode,
  }
}

export async function createArenaTrolleyToken(params: CreateArenaTrolleyTokenParams) {
  const { apiAuthToken, apiBase, perfId, selectedSeats, sendMethod } = params
  const seatGroups = getArenaReservableSeatGroups(selectedSeats)
  const sourceCode = getArenaSendMethodSourceCode(sendMethod)
  let trolleyToken: string | undefined

  for (const group of seatGroups) {
    const searchParams = new URLSearchParams({
      [`${sourceCode}_send_code`]: sendMethod.send_code,
      no_of_seats: group.seats.length.toString(),
      perf_id: perfId,
      price_band_code: group.priceBandCode,
      ticket_type_code: group.ticketTypeCode,
    })

    if (trolleyToken) searchParams.set('trolley_token', trolleyToken)

    group.seats.forEach((seat, index) => {
      searchParams.set(`disc${index}`, group.discountCode)
      searchParams.set(`seat${index}`, seat.seatId)
    })

    const response = await fetch(buildTrolleyUrl(apiBase, searchParams), {
      headers: {
        Authorization: `Bearer ${apiAuthToken}`,
      },
    })

    if (!response.ok) {
      console.warn('[Arena] trolley token request failed', {
        ...getArenaTrolleyLogContext(group, perfId),
        status: response.status,
      })
      throw new Error('Arena trolley token request failed')
    }

    trolleyToken = getArenaTrolleyResponseToken(await response.json())
    if (!trolleyToken) {
      console.warn('[Arena] trolley token response did not include a token', getArenaTrolleyLogContext(group, perfId))
      throw new Error('Arena trolley token response did not include a token')
    }
  }

  return trolleyToken
}
